/**
 * 🎯 Video System Facade
 * 動画システムの統一エントリポイント（ファサードパターン）
 *
 * 責任:
 * - 高レベルAPI提供
 * - 内部複雑性の隠蔽
 * - 既存システムとの互換性維持
 * - イベント管理
 */

import { logger } from '@/utils/logger.js';
import {
  UnifiedVideoState,
  type UnifiedVideoStateData,
  type VideoInfo,
  type TransitionType,
  type StateChangeListener,
  type TransitionRecord
} from './UnifiedVideoState.js';
import { VideoService } from '../services/VideoService.js';
import { TransitionService } from '../services/TransitionService.js';
import { TimerService } from '../services/TimerService.js';
import { VideoQueue, type VideoData } from '../VideoQueue.js';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * ファサード設定
 */
export interface FacadeConfig {
  /** 動画コンテナID */
  containerId?: string;
  /** 切り替え間隔（ミリ秒） */
  switchInterval?: number;
  /** トランジションタイムアウト（ミリ秒） */
  transitionTimeout?: number;
  /** 最大リトライ回数 */
  maxRetries?: number;
  /** デバッグモード */
  debug?: boolean;
}

/**
 * ギフト再生オプション
 */
export interface GiftPlayOptions {
  /** 再生時間（ミリ秒） */
  duration?: number;
  /** トランジションタイプ */
  transitionType?: TransitionType;
  /** 完了コールバック */
  onComplete?: () => void;
}

/**
 * イベント名
 */
export type VideoSystemEvent =
  | 'stateChange'
  | 'transitionStart'
  | 'transitionPeak'
  | 'transitionComplete'
  | 'transitionError'
  | 'videoSwitch'
  | 'giftStart'
  | 'giftEnd'
  | 'schedulerStart'
  | 'schedulerStop'
  | 'error';

/**
 * イベントコールバック
 */
export type EventCallback<T = unknown> = (data: T) => void;

/**
 * システムステータス
 */
export interface SystemStatus {
  /** 初期化済み */
  initialized: boolean;
  /** 再生中 */
  isPlaying: boolean;
  /** トランジション中 */
  isTransitioning: boolean;
  /** ギフトモード */
  giftMode: boolean;
  /** スケジューラアクティブ */
  schedulerActive: boolean;
  /** 現在の動画 */
  currentVideo: VideoInfo | null;
  /** 成功率 */
  successRate: number;
  /** キューサイズ */
  queueSize: number;
}

// ============================================================================
// VideoSystemFacade Class
// ============================================================================

/**
 * 動画システムファサード
 */
export class VideoSystemFacade {
  // 状態管理
  private state: UnifiedVideoState;

  // サービス
  private videoService: VideoService;
  private transitionService: TransitionService;
  private timerService: TimerService;
  private videoQueue: VideoQueue;

  // 設定
  private config: Required<FacadeConfig>;

  // イベントリスナー
  private eventListeners: Map<VideoSystemEvent, Set<EventCallback>>;

  // タイマーID
  private schedulerTimerId: string | null;
  private giftTimerId: string | null;

  // シングルトンインスタンス
  private static instance: VideoSystemFacade | null = null;

  constructor(config: FacadeConfig = {}) {
    // 設定
    this.config = {
      containerId: config.containerId || 'video-container',
      switchInterval: config.switchInterval || 30000,
      transitionTimeout: config.transitionTimeout || 5000,
      maxRetries: config.maxRetries || 3,
      debug: config.debug ?? false
    };

    // 状態管理
    this.state = new UnifiedVideoState({
      scheduler: {
        state: 'idle',
        nextSwitchTime: null,
        interval: this.config.switchInterval,
        retryCount: 0,
        maxRetries: this.config.maxRetries
      }
    });

    // サービス初期化
    this.videoService = new VideoService();
    this.transitionService = new TransitionService();
    this.timerService = new TimerService();
    this.videoQueue = new VideoQueue([], {});

    // イベントリスナー
    this.eventListeners = new Map();

    // タイマーID
    this.schedulerTimerId = null;
    this.giftTimerId = null;

    // 状態変更を監視
    this.state.subscribe(this.handleStateChange.bind(this));

    this.log('VideoSystemFacade created');
  }

  // ===== シングルトン =====

  /**
   * シングルトンインスタンスを取得
   */
  static getInstance(config?: FacadeConfig): VideoSystemFacade {
    if (!VideoSystemFacade.instance) {
      VideoSystemFacade.instance = new VideoSystemFacade(config);
    }
    return VideoSystemFacade.instance;
  }

  /**
   * シングルトンインスタンスをリセット
   */
  static resetInstance(): void {
    if (VideoSystemFacade.instance) {
      VideoSystemFacade.instance.cleanup();
      VideoSystemFacade.instance = null;
    }
  }

  // ===== 初期化 =====

  /**
   * 初期化
   */
  async initialize(videos?: VideoData[]): Promise<boolean> {
    try {
      this.log('Initializing VideoSystemFacade...');

      // VideoService初期化
      const videoInitialized = this.videoService.initialize(this.config.containerId);
      if (!videoInitialized) {
        throw new Error('Failed to initialize VideoService');
      }

      // TransitionService初期化
      const transitionInitialized = this.transitionService.initialize();
      if (!transitionInitialized) {
        this.log('TransitionService fallback mode');
      }

      // 動画キュー更新
      if (videos && videos.length > 0) {
        this.videoQueue.updateVideos(videos);
      }

      // 状態更新
      this.state.setInitialized(true);

      // グローバルにアクセス可能にする
      if (typeof window !== 'undefined') {
        (window as unknown as { videoSystemFacade?: VideoSystemFacade }).videoSystemFacade = this;
      }

      this.log('VideoSystemFacade initialized successfully');
      return true;

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.handleError(err, 'initialization');
      return false;
    }
  }

  // ===== 高レベルAPI =====

  /**
   * 背景動画を切り替え
   */
  async switchBackground(videoData?: VideoData): Promise<boolean> {
    if (!this.state.canTransition()) {
      this.log('Cannot switch: transition in progress');
      return false;
    }

    try {
      // 動画選択
      const video = videoData || this.videoQueue.getNext();
      if (!video || !video.src) {
        throw new Error('No video available');
      }

      this.log(`Switching to background: ${video.src}`);

      // トランジション開始
      this.state.startTransition('switch-random-overlap', 'background-switch');
      this.emit('transitionStart', { type: 'background', video });

      // トランジション実行
      await this.executeTransition(video, 'switch-random-overlap');

      // 動画切り替え
      this.state.switchVideo({
        src: video.src,
        type: 'background',
        element: this.getNextElement()
      });

      // トランジション完了
      this.state.completeTransition(true);
      this.state.resetTransition();
      this.state.resetRetryCount();

      this.emit('videoSwitch', { video, type: 'background' });
      this.emit('transitionComplete', { success: true });

      this.log('Background switch completed');
      return true;

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.handleTransitionError(err);
      return false;
    }
  }

  /**
   * ギフト動画を再生
   */
  async playGift(giftData: VideoData, options: GiftPlayOptions = {}): Promise<boolean> {
    const duration = options.duration || 8000;

    if (!this.state.canTransition()) {
      this.log('Cannot play gift: transition in progress');
      return false;
    }

    try {
      this.log(`Playing gift: ${giftData.src}`);

      // ギフトモード開始
      this.state.setGiftMode(true);
      this.emit('giftStart', { gift: giftData, duration });

      // スケジューラを一時停止
      const wasSchedulerActive = this.state.isSchedulerActive();
      if (wasSchedulerActive) {
        this.pauseScheduler();
      }

      // トランジション実行
      this.state.startTransition('switch-to-gift', 'gift-start');
      await this.executeTransition(giftData, 'switch-to-gift');

      // 動画切り替え
      this.state.switchVideo({
        src: giftData.src,
        type: 'gift',
        element: this.getNextElement()
      });

      this.state.completeTransition(true);

      // ギフト終了タイマー
      this.giftTimerId = this.timerService.scheduleOnce(duration, () => {
        void this.endGift(wasSchedulerActive);
        if (options.onComplete) {
          options.onComplete();
        }
      }, 'gift-end');

      this.log('Gift playback started');
      return true;

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.state.setGiftMode(false);
      this.handleTransitionError(err);
      return false;
    }
  }

  /**
   * ギフト終了
   */
  private async endGift(resumeScheduler: boolean): Promise<void> {
    try {
      this.log('Ending gift playback');

      // トランジションで背景に戻る
      this.state.startTransition('switch-from-gift', 'gift-end');

      const nextVideo = this.videoQueue.getNext();
      if (nextVideo) {
        await this.executeTransition(nextVideo, 'switch-from-gift');
        this.state.switchVideo({
          src: nextVideo.src,
          type: 'background',
          element: this.getNextElement()
        });
      }

      this.state.completeTransition(true);
      this.state.resetTransition();
      this.state.setGiftMode(false);

      this.emit('giftEnd', {});

      // スケジューラ再開
      if (resumeScheduler) {
        this.resumeScheduler();
      }

      this.log('Gift playback ended');

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.state.setGiftMode(false);
      this.handleError(err, 'endGift');
    }
  }

  /**
   * 即座に動画を切り替え（トランジションなし）
   */
  async switchImmediate(videoData: VideoData): Promise<boolean> {
    try {
      this.log(`Immediate switch to: ${videoData.src}`);

      // 動画を直接切り替え
      const element = this.videoService.getActiveVideoElement() || document.createElement('video');
      await this.videoService.loadVideo(element, videoData.src);
      await this.videoService.playVideo(element);

      this.state.switchVideo({
        src: videoData.src,
        type: 'background',
        element: this.getNextElement()
      });

      this.emit('videoSwitch', { video: videoData, type: 'immediate' });
      return true;

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.handleError(err, 'switchImmediate');
      return false;
    }
  }

  // ===== スケジューラ =====

  /**
   * 自動切り替えスケジューラを開始
   */
  startScheduler(interval?: number): void {
    if (this.state.isSchedulerActive()) {
      this.log('Scheduler already active');
      return;
    }

    const switchInterval = interval || this.config.switchInterval;
    this.log(`Starting scheduler (interval: ${switchInterval}ms)`);

    this.state.setSchedulerState('active', Date.now() + switchInterval);

    this.schedulerTimerId = this.timerService.startInterval(switchInterval, () => {
      if (!this.state.isGiftMode()) {
        void this.switchBackground();
        this.state.setSchedulerState('active', Date.now() + switchInterval);
      }
    }, 'background-scheduler');

    this.emit('schedulerStart', { interval: switchInterval });
  }

  /**
   * スケジューラを停止
   */
  stopScheduler(): void {
    if (this.schedulerTimerId) {
      this.timerService.stopInterval(this.schedulerTimerId);
      this.schedulerTimerId = null;
    }

    this.state.setSchedulerState('idle', null);
    this.emit('schedulerStop', {});
    this.log('Scheduler stopped');
  }

  /**
   * スケジューラを一時停止
   */
  private pauseScheduler(): void {
    if (this.schedulerTimerId) {
      this.timerService.stopInterval(this.schedulerTimerId);
      this.schedulerTimerId = null;
    }
    this.state.setSchedulerState('paused');
  }

  /**
   * スケジューラを再開
   */
  private resumeScheduler(): void {
    this.startScheduler();
  }

  // ===== トランジション実行 =====

  /**
   * トランジションを実行
   */
  private async executeTransition(video: VideoData, type: TransitionType): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Transition timeout'));
      }, this.config.transitionTimeout);

      // TransitionServiceを使用
      this.transitionService.startTransition(
        type === 'switch-random-overlap' ? 'background-switch' :
        type === 'switch-to-gift' ? 'bg-to-gift' :
        type === 'switch-from-gift' ? 'gift-to-bg' : 'fade',
        {
          onPeak: () => {
            this.emit('transitionPeak', { video, type });
          },
          onComplete: () => {
            clearTimeout(timeout);
            resolve();
          },
          onError: (error) => {
            clearTimeout(timeout);
            reject(error);
          }
        }
      ).catch(reject);
    });
  }

  /**
   * トランジションエラー処理
   */
  private handleTransitionError(error: Error): void {
    this.state.setTransitionError(error);
    this.emit('transitionError', { error });

    const retryCount = this.state.getSchedulerState().retryCount;
    const maxRetries = this.config.maxRetries;

    if (retryCount < maxRetries) {
      this.state.incrementRetryCount();
      this.log(`Retrying transition (${retryCount + 1}/${maxRetries})`);

      // 1秒後にリトライ
      this.timerService.scheduleOnce(1000, () => {
        this.state.resetTransition();
        void this.switchBackground();
      }, 'transition-retry');
    } else {
      this.log('Max retries reached, recovering...');
      this.recoverFromError();
    }
  }

  /**
   * エラーからの復旧
   */
  private recoverFromError(): void {
    this.state.resetTransition();
    this.state.resetRetryCount();
    this.state.clearError();

    // 5秒後にスケジューラ再開
    this.timerService.scheduleOnce(5000, () => {
      if (!this.state.isSchedulerActive()) {
        this.startScheduler();
      }
    }, 'error-recovery');
  }

  // ===== 状態取得 =====

  /**
   * システムステータスを取得
   */
  getStatus(): SystemStatus {
    const state = this.state.getState();
    return {
      initialized: state.system.initialized,
      isPlaying: state.system.isPlaying,
      isTransitioning: this.state.isTransitioning(),
      giftMode: state.system.giftMode,
      schedulerActive: this.state.isSchedulerActive(),
      currentVideo: state.currentVideo.src ? state.currentVideo : null,
      successRate: this.state.getSuccessRate(),
      queueSize: this.videoQueue.getStatus().totalVideos
    };
  }

  /**
   * 完全な状態を取得
   */
  getState(): Readonly<UnifiedVideoStateData> {
    return this.state.getState();
  }

  /**
   * トランジション履歴を取得
   */
  getHistory(): Readonly<TransitionRecord[]> {
    return this.state.getHistory();
  }

  // ===== イベント管理 =====

  /**
   * イベントリスナー登録
   */
  on<T = unknown>(event: VideoSystemEvent, callback: EventCallback<T>): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback as EventCallback);

    // unsubscribe関数を返す
    return () => this.off(event, callback);
  }

  /**
   * イベントリスナー削除
   */
  off<T = unknown>(event: VideoSystemEvent, callback: EventCallback<T>): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback as EventCallback);
    }
  }

  /**
   * イベント発火
   */
  private emit(event: VideoSystemEvent, data: unknown): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          logger.error(`Event listener error (${event}):`, error);
        }
      });
    }
  }

  /**
   * 状態変更ハンドラ
   */
  private handleStateChange(
    newState: Readonly<UnifiedVideoStateData>,
    previousState: Readonly<UnifiedVideoStateData>,
    _changes: unknown
  ): void {
    this.emit('stateChange', { newState, previousState });
  }

  // ===== 状態購読 =====

  /**
   * 状態変更を購読
   */
  subscribe(listener: StateChangeListener): () => void {
    return this.state.subscribe(listener);
  }

  // ===== ユーティリティ =====

  /**
   * 次の動画要素を取得
   */
  private getNextElement(): 'A' | 'B' {
    const current = this.state.getCurrentVideo().element;
    return current === 'A' ? 'B' : 'A';
  }

  /**
   * エラーハンドリング
   */
  private handleError(error: Error, context: string): void {
    logger.error(`VideoSystemFacade error (${context}):`, error);
    this.emit('error', { error, context, timestamp: Date.now() });
  }

  /**
   * ログ出力
   */
  private log(message: string): void {
    if (this.config.debug) {
      logger.info(`[VideoSystemFacade] ${message}`);
    }
  }

  // ===== クリーンアップ =====

  /**
   * クリーンアップ
   */
  cleanup(): void {
    // タイマー停止
    this.stopScheduler();
    if (this.giftTimerId) {
      this.timerService.cancelScheduled(this.giftTimerId);
    }
    this.timerService.stopAll();

    // サービスクリーンアップ
    this.videoService.cleanup();

    // リスナークリア
    this.eventListeners.clear();

    // 状態リセット
    this.state.reset();

    this.log('VideoSystemFacade cleaned up');
  }

  /**
   * システムリセット
   */
  reset(): void {
    this.stopScheduler();
    this.state.reset();
    this.state.setInitialized(true);
    this.log('VideoSystemFacade reset');
  }

  // ===== 後方互換性API =====

  /**
   * 後方互換: playNextBackgroundVideo
   */
  async playNextBackgroundVideo(): Promise<boolean> {
    return this.switchBackground();
  }

  /**
   * 後方互換: playGiftVideo
   */
  async playGiftVideo(giftData: VideoData, duration?: number): Promise<boolean> {
    return this.playGift(giftData, { duration });
  }

  /**
   * 後方互換: startTimer
   */
  startTimer(interval?: number): void {
    this.startScheduler(interval);
  }

  /**
   * 後方互換: stopTimer
   */
  stopTimer(): void {
    this.stopScheduler();
  }
}

// Export
export default VideoSystemFacade;
